// Price Notice Email Processor
// Parses wholesale fuel cost from price notice email attachments

import type { WholesalePrice } from '@jrd/core';

export interface PriceNoticeConfig {
  email: string;
  folder?: string;
  subjectPattern?: string;
}

export interface ParsedPriceNotice {
  supplier: string;
  effectiveDate: Date;
  costs: {
    grade: '87' | '89' | '91' | 'diesel';
    grossCost: number;
    taxes: number;
    netCost: number; // This is what we use for margin calculation
  }[];
  rawText?: string;
}

// Known supplier patterns
const SUPPLIER_PATTERNS = {
  circleK: {
    name: 'Circle K',
    patterns: ['circle k', 'circlektexas', 'couche-tard', 'holiday stationstores'],
    costLabel: ['rack price', 'dealer price', 'cost per gallon'],
    taxLabel: ['state tax', 'federal tax', 'total tax'],
  },
  waterford: {
    name: 'Waterford Oil',
    patterns: ['waterford', 'world fuel'],
    costLabel: ['net cost', 'dealer cost'],
    taxLabel: ['tax'],
  },
  mapleLeaf: {
    name: 'Maple Leaf Oil',
    patterns: ['maple leaf'],
    costLabel: ['cost'],
    taxLabel: ['tax'],
  },
};

// Parse price from text (handles various formats)
function parsePrice(text: string): number | null {
  // Match patterns like: $2.089, 2.089, 208.9¢
  const dollarMatch = text.match(/\$?\s*(\d+\.\d{2,4})/);
  if (dollarMatch) {
    return parseFloat(dollarMatch[1]);
  }

  const centsMatch = text.match(/(\d+\.?\d*)¢/);
  if (centsMatch) {
    return parseFloat(centsMatch[1]) / 100;
  }

  return null;
}

// Parse date from text
function parseDate(text: string): Date | null {
  // Try common date formats
  const patterns = [
    /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/,  // MM/DD/YYYY
    /(\d{1,2})-(\d{1,2})-(\d{2,4})/,     // MM-DD-YYYY
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      try {
        return new Date(match[0]);
      } catch {
        continue;
      }
    }
  }

  return null;
}

// Extract fuel grade from context
function extractGrade(line: string): '87' | '89' | '91' | 'diesel' | null {
  const lower = line.toLowerCase();
  if (lower.includes('regular') || lower.includes('87') || lower.includes('unleaded')) return '87';
  if (lower.includes('plus') || lower.includes('89') || lower.includes('mid')) return '89';
  if (lower.includes('premium') || lower.includes('91') || lower.includes('93')) return '91';
  if (lower.includes('diesel') || lower.includes('dsl')) return 'diesel';
  return null;
}

// Main parser for price notice text
export function parsePriceNotice(text: string): ParsedPriceNotice | null {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  // Detect supplier
  let supplier = 'Unknown';
  const lowerText = text.toLowerCase();
  for (const [key, config] of Object.entries(SUPPLIER_PATTERNS)) {
    if (config.patterns.some(p => lowerText.includes(p))) {
      supplier = config.name;
      break;
    }
  }

  // Extract effective date
  let effectiveDate = new Date();
  for (const line of lines) {
    if (line.toLowerCase().includes('effective') || line.toLowerCase().includes('date')) {
      const date = parseDate(line);
      if (date) {
        effectiveDate = date;
        break;
      }
    }
  }

  // Extract costs by grade
  const costs: ParsedPriceNotice['costs'] = [];

  // Strategy: Look for lines with grade indicators and prices
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const grade = extractGrade(line);

    if (grade) {
      // Look for price in current line or next few lines
      let grossCost = 0;
      let taxes = 0;
      let netCost = 0;

      // Search in a window around this line
      for (let j = Math.max(0, i - 1); j < Math.min(lines.length, i + 4); j++) {
        const searchLine = lines[j].toLowerCase();

        // Look for cost labels
        if (searchLine.includes('cost') || searchLine.includes('rack') || searchLine.includes('dealer')) {
          const price = parsePrice(lines[j]);
          if (price && price < 10) { // Sanity check: fuel price should be under $10
            grossCost = price;
          }
        }

        // Look for tax amounts
        if (searchLine.includes('tax')) {
          const price = parsePrice(lines[j]);
          if (price && price < 2) { // Taxes typically under $2/gal
            taxes += price;
          }
        }

        // Look for net/total cost
        if (searchLine.includes('net') || searchLine.includes('total')) {
          const price = parsePrice(lines[j]);
          if (price && price < 10) {
            netCost = price;
          }
        }
      }

      // If we found any pricing data for this grade
      if (grossCost > 0 || netCost > 0) {
        // Calculate net cost if not explicitly found
        if (netCost === 0) {
          netCost = grossCost + taxes;
        }

        // Only add if we don't already have this grade
        if (!costs.find(c => c.grade === grade)) {
          costs.push({
            grade,
            grossCost,
            taxes,
            netCost,
          });
        }
      }
    }
  }

  // Fallback: if no structured data found, try to find any 87 unleaded cost
  if (costs.length === 0) {
    // Look for common patterns like "87 Unleaded: $2.089"
    for (const line of lines) {
      const grade = extractGrade(line);
      const price = parsePrice(line);

      if (grade && price && price < 10) {
        costs.push({
          grade,
          grossCost: price,
          taxes: 0,
          netCost: price,
        });
      }
    }
  }

  if (costs.length === 0) {
    console.error('Could not parse any fuel costs from price notice');
    return null;
  }

  return {
    supplier,
    effectiveDate,
    costs,
    rawText: text,
  };
}

// Convert parsed notice to wholesale price for a specific grade
export function toWholesalePrice(
  locationId: string,
  notice: ParsedPriceNotice,
  grade: '87' | '89' | '91' | 'diesel' = '87'
): WholesalePrice | null {
  const cost = notice.costs.find(c => c.grade === grade);

  if (!cost) return null;

  return {
    locationId,
    supplier: notice.supplier,
    grade,
    cost: cost.netCost, // Use net cost (after tax) for margin calculation
    effectiveDate: notice.effectiveDate,
    source: 'email',
  };
}

// Process price notice attachment (handles PDF extraction)
export async function processPriceNoticeAttachment(
  file: File
): Promise<ParsedPriceNotice | null> {
  try {
    // For PDF files, we need to extract text first
    if (file.type === 'application/pdf') {
      // Use the OCR service or PDF.js
      const text = await extractTextFromPDF(file);
      return parsePriceNotice(text);
    }

    // For text files, read directly
    if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
      const text = await file.text();
      return parsePriceNotice(text);
    }

    console.error('Unsupported file type:', file.type);
    return null;
  } catch (error) {
    console.error('Failed to process price notice:', error);
    return null;
  }
}

// Extract text from PDF (simplified - uses API endpoint)
async function extractTextFromPDF(file: File): Promise<string> {
  const apiUrl = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_URL || 'https://jrd-fuel-auto-api.jrdcompanies.workers.dev';

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${apiUrl}/api/ocr/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.text || '';
  } catch (error) {
    console.error('OCR extraction failed:', error);
    // Fallback: return empty string (will fail parsing but won't crash)
    return '';
  }
}

// Configuration for email monitoring
export interface EmailMonitorConfig {
  email: string;
  password?: string;
  server?: string;
  folder: string;
  subjectFilter: string;
  autoProcess: boolean;
}

// Store email config in localStorage
export function saveEmailConfig(locationId: string, config: EmailMonitorConfig): void {
  const key = `priceNotice_${locationId}`;
  localStorage.setItem(key, JSON.stringify(config));
}

export function loadEmailConfig(locationId: string): EmailMonitorConfig | null {
  const key = `priceNotice_${locationId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : null;
}
